/**
 * =============================================================================
 * TENANT PUBLIC LAUNCH MODE (Phase 4.5 controlled-launch slice)
 * =============================================================================
 *
 * Authoritative, tenant-level control over whether a store's public storefront
 * widget / page is usable by customers. This is the SINGLE SOURCE OF TRUTH for
 * the `publicLaunchMode` union and the helpers that resolve / interpret it,
 * shared by the worker (enforcement + config), the storefront widget
 * (rendering), and the admin UI (control surface).
 *
 * DESIGN NOTES
 * ------------
 *   - FAIL CLOSED: any missing, legacy, or unrecognised value resolves to
 *     HIDDEN. A store is never treated as publicly open unless it explicitly
 *     resolves to VISIBLE.
 *   - Submissions (questions / replies / uploads) are permitted ONLY when the
 *     resolved mode is VISIBLE. HIDDEN and COMING_SOON both suppress the public
 *     surface; they differ only in the customer-facing copy.
 *   - This control is INDEPENDENT of store lifecycle (`isActive` /
 *     `lifecycle_state`), MSPL `publishing_profiles.status`, and
 *     `public_pages` indexing flags. It must never be conflated with them.
 *
 * Persisted on `stores.settings.publicLaunchMode` (JSON; see
 * `StoreSettings` in the Drizzle schema). No dedicated D1 column is required.
 * =============================================================================
 */

/**
 * Tenant public launch mode.
 *
 *   HIDDEN       — public surface suppressed; "not currently accepting questions".
 *   COMING_SOON  — public surface suppressed; "coming soon" framing.
 *   VISIBLE      — normal public operation; submissions accepted.
 */
export type PublicLaunchMode = 'HIDDEN' | 'COMING_SOON' | 'VISIBLE';

/** All valid modes, in admin display order (most-restrictive first). */
export const PUBLIC_LAUNCH_MODES: readonly PublicLaunchMode[] = [
  'HIDDEN',
  'COMING_SOON',
  'VISIBLE',
] as const;

/**
 * Safe default applied to any missing / legacy / invalid value. Fail-closed:
 * a store with no explicit setting is treated as HIDDEN, not VISIBLE.
 */
export const DEFAULT_PUBLIC_LAUNCH_MODE: PublicLaunchMode = 'HIDDEN';

/**
 * Customer-facing copy shown by the widget when the public surface is
 * suppressed. VISIBLE has no message (the normal widget renders instead).
 */
export const PUBLIC_LAUNCH_MESSAGES = {
  HIDDEN: 'Find My Part is not currently accepting questions.',
  COMING_SOON: 'Find My Part is coming soon and is not accepting questions yet.',
} as const;

/** Short human-readable label per mode, for the admin control surface. */
export const PUBLIC_LAUNCH_LABELS: Record<PublicLaunchMode, string> = {
  HIDDEN: 'Hidden',
  COMING_SOON: 'Coming Soon',
  VISIBLE: 'Visible',
};

/** Type guard: is the value exactly one of the three valid modes? */
export function isPublicLaunchMode(value: unknown): value is PublicLaunchMode {
  return value === 'HIDDEN' || value === 'COMING_SOON' || value === 'VISIBLE';
}

/**
 * Fail-closed resolver. Returns the value unchanged when it is a valid mode,
 * otherwise {@link DEFAULT_PUBLIC_LAUNCH_MODE} (HIDDEN). Use this everywhere a
 * persisted / untrusted value is read.
 */
export function resolvePublicLaunchMode(value: unknown): PublicLaunchMode {
  return isPublicLaunchMode(value) ? value : DEFAULT_PUBLIC_LAUNCH_MODE;
}

/** Whether public customer submissions are allowed for a resolved mode. */
export function isPublicSubmissionAllowed(mode: PublicLaunchMode): boolean {
  return mode === 'VISIBLE';
}

/**
 * Customer-facing unavailable message for a resolved mode, or `null` when the
 * mode is VISIBLE (no message — the normal widget renders).
 */
export function publicUnavailableMessage(mode: PublicLaunchMode): string | null {
  if (mode === 'HIDDEN') return PUBLIC_LAUNCH_MESSAGES.HIDDEN;
  if (mode === 'COMING_SOON') return PUBLIC_LAUNCH_MESSAGES.COMING_SOON;
  return null;
}

/**
 * Sanitized public availability shape returned by
 * `GET /api/public/availability` and consumed by the storefront widget.
 *
 * Contains NO secrets, tokens, headers, or store identifiers — only the
 * resolved mode and its derived, customer-safe presentation fields.
 */
export interface PublicAvailability {
  /** Resolved tenant launch mode (already fail-closed). */
  launchMode: PublicLaunchMode;
  /** Convenience flag: `true` ⟺ `launchMode === 'VISIBLE'`. */
  submissionsEnabled: boolean;
  /** Customer-facing message when suppressed; `null` when VISIBLE. */
  unavailableMessage: string | null;
}

/** Build a {@link PublicAvailability} from a resolved mode. */
export function buildPublicAvailability(mode: PublicLaunchMode): PublicAvailability {
  return {
    launchMode: mode,
    submissionsEnabled: isPublicSubmissionAllowed(mode),
    unavailableMessage: publicUnavailableMessage(mode),
  };
}
